
import { Button } from "@/components/ui/button";
import { Save } from "lucide-react";
import { Canvas } from "fabric";
import { useState, useEffect } from "react";
import { ReactNode } from "react";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { SaveDrawingDialog } from "./save-drawing/SaveDrawingDialog";
import { useDrawingSave } from "@/hooks/drawing/use-drawing-save";
import { toast } from "@/hooks/use-toast";
import { CustomCanvas } from "@/types/fabric";
import { useVersionControl } from "@/hooks/drawing/use-version-control";
import { SaveButton } from "./save-drawing/SaveButton";
import { ConflictDialog } from "./save-drawing/ConflictDialog";

interface SaveDrawingSectionProps {
  canvas: Canvas | null;
  onRefreshNeeded: (refreshFn: () => void) => void;
  title: string;
  onTitleChange: (title: string) => void;
  currentDrawingId: string | null;
  onDrawingSaved: (id: string) => void;
  customTrigger?: ReactNode;
}

export const SaveDrawingSection = ({
  canvas,
  onRefreshNeeded,
  title,
  onTitleChange,
  currentDrawingId,
  onDrawingSaved,
  customTrigger
}: SaveDrawingSectionProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [localTitle, setLocalTitle] = useState(title || "");
  const [description, setDescription] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isSaveAs, setIsSaveAs] = useState(false);
  
  const { getCanvasVersion } = useVersionControl();

  // Initialize the drawing save hook with all state handling
  const { 
    handleQuickSave, 
    handleSaveAs, 
    handleRetry,
    isSaving, 
    hasTimedOut,
    hasError,
    showConflictDialog,
    setShowConflictDialog,
    handleConflictResolution,
    conflictType
  } = useDrawingSave({
    canvas,
    currentDrawingId,
    title: localTitle,
    onDrawingSaved,
    onRefreshNeeded,
  });

  // Keep local title in sync with parent
  useEffect(() => {
    setLocalTitle(title || "");
  }, [title]);

  // Handle title changes both locally and in parent
  const handleTitleChange = (newTitle: string) => {
    setLocalTitle(newTitle);
    onTitleChange(newTitle);
  };

  // Handle description changes
  const handleDescriptionChange = (newDescription: string) => {
    setDescription(newDescription);
  };

  // Handle tag changes
  const handleTagsChange = (newTags: string[]) => {
    setSelectedTags(newTags);
  };

  // Open dialog for Save or Save As
  const openDialog = (saveAs: boolean) => {
    setIsSaveAs(saveAs);
    setIsDialogOpen(true);
  };

  // Handle the quick save action - convert boolean to void return type
  const onQuickSaveAction = async () => {
    if (!localTitle.trim()) {
      toast({
        variant: "destructive",
        title: "Title required",
        description: "Please enter a title for your drawing before saving.",
      });
      return;
    }
    
    await handleQuickSave(description, selectedTags);
    // Close dialog if it was open
    setIsDialogOpen(false);
  };

  // Handle the save as action - convert boolean to void return type
  const onSaveAsAction = async () => {
    if (!localTitle.trim()) {
      toast({
        variant: "destructive",
        title: "Title required",
        description: "Please enter a title for your drawing before saving.",
      });
      return;
    }
    
    await handleSaveAs(description, selectedTags);
    setIsDialogOpen(false);
  };

  // Handle retry action - convert boolean to void return type
  const onRetryAction = async () => {
    await handleRetry();
  };

  return (
    <div className="relative">
      <SaveButton
        onQuickSave={onQuickSaveAction}
        onSaveAs={onSaveAsAction}
        onRetry={onRetryAction}
        isSaving={isSaving}
        hasTimedOut={hasTimedOut}
        hasError={hasError}
        title={localTitle}
        onTitleChange={handleTitleChange}
        saveDescription={description}
        onDescriptionChange={handleDescriptionChange}
        selectedTags={selectedTags}
        onTagsChange={handleTagsChange}
        saveAsDialogOpen={isDialogOpen}
        setSaveAsDialogOpen={setIsDialogOpen}
        currentDrawingId={currentDrawingId}
      />
      
      {/* Version Conflict Resolution Dialog */}
      <ConflictDialog
        open={showConflictDialog}
        onOpenChange={setShowConflictDialog}
        onReloadNewest={() => handleConflictResolution('reload')}
        onForceOverwrite={() => handleConflictResolution('overwrite')}
        onSaveAsCopy={() => handleConflictResolution('copy')}
        conflictType={conflictType || 'minor'}
      />
    </div>
  );
};
